/**
 * src/lib/tagCorruptionWarning.ts
 *
 * backlog #46239 — three authorization posts on 2026-09-17 carried tags with
 * a reserved prefix corrupted by one character (`rack:backlog-...` instead
 * of `track:backlog-...`; one post also lost `reply-to:` the same way). §3-D
 * unblocks a track only on a matching `track:{id}` tag and §5 detects HIGH
 * authorization the same way — a corrupted tag matches nothing, so the gate
 * never clears, the agent stays blocked, and the human who posted the
 * approval has no way to know it didn't work. Nothing anywhere reported it;
 * an advisor audit of the raw tags was what caught it.
 *
 * Same posture as projectTagWarning.ts alongside this file: advisory only,
 * never a hard block (§34-D's precedent, and the backlog item's own
 * proposal — "a warning the author must dismiss is enough"). Pure functions,
 * no Firestore/React dependency, so they're testable standalone and usable
 * from ERComposerV1.tsx without pulling in any of its state.
 *
 * Two checks, deliberately not three — the backlog item proposed a third
 * (compare a track: tag against the active sprint's known track list) that
 * would need a live sprint-tracks subscription threaded into the composer,
 * a separate design question. See track:composer-tag-corruption-warning's
 * pre-flight for the reasoning; filed as its own backlog item.
 *
 * A third check was added later (spec #46707, backlog #46665) for a
 * different failure than either of the above: a gate-clearing tag posted
 * with no track: tag at all, rather than a corrupted or disagreeing one —
 * see findMissingTrackTagOnGateClearingPost below.
 *
 * A fourth and fifth check were added for backlog #46961: nothing validated
 * a gate-clearing post's *tag vocabulary* or *anchor* at write time. Three
 * malformed posts shipped in one day — #46916 anchored with bare numeric
 * tags ("46905") instead of reply-to:46905, and #46960 used the tag
 * `approved` (not a member of the scheduler's unblock-tag set) to try to
 * clear a gate. Both were accepted by the composer, cleared nothing, and
 * produced no error — see findUnrecognizedGateClearingReply and
 * findGateClearingTagWithNoAnchor below.
 */

import { TAG, parseReplyToTag } from "./teamLogTags";

// Every reserved tag prefix §3-D/§5/the scheduler key gates off of, or that
// carries structural meaning elsewhere in Relay (project scoping, agent
// identity). Not every tag with a colon is reserved — e.g. `tier:`, `blast:`,
// `status:`, `feature:`, `repo:`, `spec:` are all in active use and must
// never trip this check; each is edit-distance ≥3 from every prefix below,
// confirmed before shipping this list, not assumed.
export const RESERVED_TAG_PREFIXES: readonly string[] = [
  "track:",
  "reply-to:",
  "sprint:",
  "project:",
  "agent:",
];

// Standard Levenshtein edit distance (insertions + deletions + substitutions).
// No dependency pulled in for this — it's ~15 lines and the alternative is a
// package for a single-digit-length string comparison.
function levenshteinDistance(a: string, b: string): number {
  const rows = a.length + 1;
  const cols = b.length + 1;
  const d: number[][] = Array.from({ length: rows }, () => new Array(cols).fill(0));
  for (let i = 0; i < rows; i++) d[i][0] = i;
  for (let j = 0; j < cols; j++) d[0][j] = j;
  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1, // deletion
        d[i][j - 1] + 1, // insertion
        d[i - 1][j - 1] + cost, // substitution
      );
    }
  }
  return d[rows - 1][cols - 1];
}

/**
 * A tag that doesn't start with any reserved prefix, but whose own
 * colon-delimited lead-in (everything up to and including its first colon)
 * is within edit-distance 1 of one. Compares against the tag's own lead-in,
 * not a fixed-length slice of the whole tag — a fixed window fails on
 * exactly the real corruption (`"rack:backlog-..."`.slice(0, 6)` = `"rack:b"`,
 * which is distance 2 from `"track:"`, not 1 — the extra character from the
 * long suffix breaks a fixed-length comparison). The tag's own lead-in
 * (`"rack:"`) is distance 1, correctly.
 *
 * Returns the reserved prefix the tag most likely corrupted, or null.
 */
export function likelyCorruptedReservedPrefix(
  tag: string,
  reservedPrefixes: readonly string[] = RESERVED_TAG_PREFIXES,
): string | null {
  if (reservedPrefixes.some((p) => tag.startsWith(p))) return null;
  const colonIdx = tag.indexOf(":");
  if (colonIdx === -1) return null;
  const leadIn = tag.slice(0, colonIdx + 1);
  for (const prefix of reservedPrefixes) {
    if (Math.abs(leadIn.length - prefix.length) > 1) continue; // can't be distance ≤1
    if (levenshteinDistance(leadIn, prefix) <= 1) return prefix;
  }
  return null;
}

// Matches a `track:{id}` reference inside free-typed body text. Track ids
// seen in this workspace are hyphen/underscore/dot-separated slugs, possibly
// numeric backlog references — matches the same shape `sanitizedProjectTag`
// and friends use elsewhere in this codebase.
const BODY_TRACK_REF_RE = /\btrack:([a-zA-Z0-9][a-zA-Z0-9._/-]*)/g;

/**
 * The "second, cheaper check" from backlog #46239: the post body contains
 * `track:{id}`-shaped reference(s), and *none* of them match the tags
 * array's own `track:` tag. Catches #46233's near-miss (body authorized one
 * track, tag named another, and the tag's id appeared nowhere in the body)
 * — human error, not input corruption, but invisible in exactly the same
 * way.
 *
 * Tightened per backlog #46518: a body legitimately mentioning multiple
 * tracks (e.g. "supersedes track:foo, authorizing track:bar", tagged
 * track:bar) must not warn just because the first reference differs — only
 * when every body reference disagrees with the tag is the post actually
 * suspect.
 *
 * Returns a differing id (for display), or null when there's no `track:`
 * tag, no body reference, or at least one body reference agrees with the
 * tag.
 */
export function findBodyTagTrackMismatch(
  content: string,
  tags: string[],
): { bodyTrackId: string; tagTrackId: string } | null {
  const tagTrackId = tags.find((t) => t.startsWith("track:"))?.slice("track:".length);
  if (!tagTrackId) return null;
  const bodyMatches = [...content.matchAll(BODY_TRACK_REF_RE)].map((m) => m[1]);
  if (bodyMatches.length === 0) return null;
  if (bodyMatches.includes(tagTrackId)) return null;
  return { bodyTrackId: bodyMatches[0], tagTrackId };
}

// backlog #46665 / spec #46707 — the tags that close (clear) the §3-D
// Finding Gate per its own UNBLOCK CONDITION: "A [finding-acknowledged] or
// [pre-flight-approved] post ... unblocks the agent." Sourced from
// teamLogTags.ts's TAG constants rather than re-hardcoded here, so this list
// can't independently drift from that file's own §3-D-derived values; if
// §3-D itself changes, teamLogTags.ts's TAG.FINDING_ACKNOWLEDGED /
// TAG.PRE_FLIGHT_APPROVED is the one place to update, and this list follows.
// (Reading the live Charter at compose time isn't available here — this
// module is intentionally dependency-free, see file header — so per the
// spec's fallback this is the hardcoded-from-§3-D path, just expressed via
// the existing shared constant instead of a second copy.)
export const GATE_CLEARING_TAGS: readonly string[] = [TAG.FINDING_ACKNOWLEDGED, TAG.PRE_FLIGHT_APPROVED];

/**
 * A post carries a gate-clearing tag (per GATE_CLEARING_TAGS) but no tag
 * matching `^track:` — the failure behind seq #46649: a pre-flight-approved
 * post landed with no track: tag at all (its author typed the non-tag
 * string "track-from-46638" instead), so no blocked agent could find their
 * own approval by filtering on track:, and stayed blocked reading it.
 *
 * Unlike the other two checks above, there's no corrupted or disagreeing
 * tag to point at — the failure is an absence. Returns the offending
 * gate-clearing tag (for display), or null when there's no gate-clearing
 * tag present, or a track: tag is present alongside it.
 */
export function findMissingTrackTagOnGateClearingPost(tags: string[]): { gateClearingTag: string } | null {
  const gateClearingTag = tags.find((t) => GATE_CLEARING_TAGS.includes(t));
  if (!gateClearingTag) return null;
  if (tags.some((t) => t.startsWith("track:"))) return null;
  return { gateClearingTag };
}

// backlog #46961 — the full unblock-tag vocabulary the scheduler's own
// _FINDING_GATE_UNBLOCK_TAGS recognizes (adk-relay/deterministic_scheduler.py
// :428-432) and Charter §3-D's UNBLOCK CONDITION names: finding-acknowledged,
// pre-flight-approved (canonical), the legacy "preflight-approved"
// misspelling (historical posts only — never write it), and blocker-resolved.
// Deliberately a superset of GATE_CLEARING_TAGS above, which exists for a
// narrower purpose (the missing-track-tag check) and predates this list —
// left untouched rather than merged, to avoid changing that check's
// established behavior as a side effect of this one.
export const GATE_UNBLOCK_TAGS: readonly string[] = [
  TAG.FINDING_ACKNOWLEDGED,
  TAG.PRE_FLIGHT_APPROVED,
  TAG.PRE_FLIGHT_APPROVED_LEGACY,
  TAG.BLOCKER_RESOLVED,
];

/**
 * Whether `tags`/`replyToSequence` resolve to *any* anchor at all — either a
 * `reply-to:{seq}` tag (parsed via parseReplyToTag, so a bare numeric tag
 * like "46905" does NOT count — that's #46916's exact failure) or the
 * composer's own reply-intent state (relay-47's `replyToSequence`, set by
 * clicking a #NNN chip).
 */
function hasResolvableAnchor(tags: string[], replyToSequence: number | null): boolean {
  if (replyToSequence !== null) return true;
  return tags.some((t) => parseReplyToTag(t) !== null);
}

/**
 * backlog #46961, AC1 — a post that's anchored to another message (a
 * `reply-to:{seq}` tag, or the composer's reply-intent state) but carries
 * none of GATE_UNBLOCK_TAGS. Catches #46960: a reply tagged `approved`
 * instead of `pre-flight-approved` — correctly anchored, but the tag the
 * scheduler actually checks for was never there, so the gate cleared
 * nothing and nobody was told.
 *
 * Returns the recognized-tag list (for display), or null when there's no
 * anchor to begin with, or the post already carries a recognized tag.
 */
export function findUnrecognizedGateClearingReply(
  tags: string[],
  replyToSequence: number | null,
): { recognizedTags: readonly string[] } | null {
  if (!hasResolvableAnchor(tags, replyToSequence)) return null;
  if (tags.some((t) => GATE_UNBLOCK_TAGS.includes(t))) return null;
  return { recognizedTags: GATE_UNBLOCK_TAGS };
}

/**
 * backlog #46961, AC2 — a post carrying a recognized unblock tag but no
 * resolvable anchor. Catches #46916: two approval posts tagged
 * `pre-flight-approved` with bare numeric tags ("46905", "46908") instead of
 * `reply-to:46905` / `reply-to:46908` — parseReplyToTag requires the literal
 * `reply-to:` prefix, so those tags anchored nothing and the gate stayed
 * shut.
 *
 * Returns the offending unblock tag (for display), or null when there's no
 * unblock tag present, or an anchor is already resolvable.
 */
export function findGateClearingTagWithNoAnchor(
  tags: string[],
  replyToSequence: number | null,
): { tag: string } | null {
  const tag = tags.find((t) => GATE_UNBLOCK_TAGS.includes(t));
  if (!tag) return null;
  if (hasResolvableAnchor(tags, replyToSequence)) return null;
  return { tag };
}
